/**
 * authToken.js — Resolver del bearer token compartido por axios.
 *
 * Cuando Clerk está activo, el componente <ClerkSessionSync> empuja periódicamente
 * el token JWT corto en este módulo. El interceptor de axios (api.js) lo prefiere
 * sobre el legacy `bomberosToken` cuando está disponible.
 */
let _clerkToken = null

export function setClerkToken(token) {
  _clerkToken = token || null
}

export function getClerkToken() {
  return _clerkToken
}

export function clearClerkToken() {
  _clerkToken = null
}

// --- Aliases provider-agnósticos (Supabase usa estos; comparten el mismo slot) ---
export const setSessionToken = setClerkToken
export const getSessionToken = getClerkToken
export const clearSessionToken = clearClerkToken

// Lee el access_token de la sesión de Supabase directamente de localStorage.
// Clave por defecto de supabase-js: `sb-<ref>-auth-token` (ref = subdominio de
// VITE_SUPABASE_URL). Devuelve el token solo si la sesión NO está vencida.
function supabaseTokenFromStorage() {
  try {
    const url = import.meta.env.VITE_SUPABASE_URL || ''
    const ref = url.match(/https?:\/\/([^.]+)\./)?.[1]
    if (!ref) return null
    const raw = localStorage.getItem(`sb-${ref}-auth-token`)
    if (!raw) return null
    const o = JSON.parse(raw)
    const exp = o?.expires_at
    // margen de 5s para no usar un token a punto de vencer (supabase lo refresca)
    if (o?.access_token && (!exp || exp * 1000 > Date.now() + 5000)) {
      return o.access_token
    }
  } catch {
    /* noop */
  }
  return null
}

/**
 * Devuelve el token a usar en Authorization. Prioridad:
 *   1. Token en memoria (empujado por SupabaseSessionSync/ClerkSessionSync)
 *   2. Sesión de Supabase leída de localStorage (evita el race del boot:
 *      requests que salían antes de setear el token en memoria daban 401 y
 *      el interceptor expulsaba al login al refrescar)
 *   3. legacy localStorage 'bomberosToken'
 */
export function resolveAuthToken() {
  if (_clerkToken) return _clerkToken
  const sbToken = supabaseTokenFromStorage()
  if (sbToken) return sbToken
  try {
    return localStorage.getItem('bomberosToken')
  } catch {
    return null
  }
}
